// Shared plans configuration and utility functions
// This is a copy of the plans logic from apps/app/src/app/config/plans.js
// to be used across the entire application (frontend and backend)

// Default warning threshold for all plans (80%)
export const USAGE_WARNING_THRESHOLD = 0.8;

// Free tier (no subscription, trial expired) limits
export const FREE_TIER_LIMITS = {
    maxFiles: 50,
    maxDatabaseRows: 100,
};

// Trial tier (new company, Company.trialEndDate still in future, no Stripe sub).
// Generous enough to build something meaningful but finite so the upgrade
// decision still gets forced. Matches the 10:1 row:file ratio of paid plans.
export const TRIAL_TIER_LIMITS = {
    maxFiles: 100,
    maxDatabaseRows: 1000,
};

const PLANS = [
    {
        name: "Starter",
        nameKey: "starter",
        hidden: true,
        id: "price_1SRdTTKVJm3st1cQfp3ZwHOy", // Monthly price ID
        idYearly: "price_1R94xZKVJm3st1cQQZSVWO0d_yearly", // Yearly price ID - to be updated
        productId: "prod_TOQKMhyj3H2Bu1",

        priceIdMonthly: "price_1SRdTTKVJm3st1cQfp3ZwHOy",
        priceIdYearly: "price_1SRdTTKVJm3st1cQfsX0KdL5",

        devPriceIdMonthly: "price_1R95yJKVJm3st1cQ6BdBy3Mw",

        devProductId: "prod_S3CN1lRq0y6cKu", // Development product ID

        description: "For individuals and small teams getting started",
        numberOfUsers: "3",
        price: "20",
        priceYearly: "16",
        credits: "80",
        supportLevel: "none", // No support included
        features: [
            "Remove AgentUI Badge",
            "Unlimited number of apps",
            "Unlimited External Users",
            "3 Internal Users",
        ],
        featureKeys: [
            "removeAgentUIBadge",
            "unlimitedApps",
            "unlimitedExternalUsers",
            "internalUsers3",
        ],
        enabledFeatures: {
            WHATSAPP: false,
            integrations: false,
            backendFunctions: false,
            exportApp: false,
            inAppCodeEdits: false,
            sharedSecrets: false,
            sharedDatabases: false,
            deepLinking: false,
            facebookAds: false,
            clientPortal: false,
            multipleEnvironments: false,
            enviroments: false,
            productionLock: false,
            customDomains: false,
            auditLogs: false,
            codeScanning: false,
            sso: false,
            dataObfuscation: false,
            designSystem: false,
            complianceOfficer: false,
            aiTesting: false,
        },
        automationExecutions: 500,
        automation: {
            monthlyLimit: 500,
            graceMultiplier: 1.5,
            maxConcurrent: 2,
        },
        usageLimits: { maxFiles: 500, maxDatabaseRows: 5000 },
    },
    {
        name: "Minimum Package",
        nameKey: "minimumPackage",
        id: "price_1SRdW0KVJm3st1cQ8dKYcoHZ", // Monthly price ID
        idYearly: "price_1R4LGzKVJm3st1cQsPsFA4SC_yearly", // Yearly price ID - to be updated
        productId: "prod_TOQM5MvsRe366q",
        devProductId: "prod_TkFmwm4ZVlwg42", // Development product ID

        priceIdMonthly: "price_1SRdW0KVJm3st1cQ8dKYcoHZ",
        priceIdYearly: "price_1SRdW0KVJm3st1cQNG0NWcPA",

        devPriceIdMonthly: "price_1SmlH6KVJm3st1cQY4erB7zV",
        devPriceIdYearly: "price_1SmlH6KVJm3st1cQUkKY5zBO",

        descriptionKey: "billing.planDescriptions.minimumPackage",
        description:
            "The minimum requirement to start building functional and real tools",
        price: "50",
        numberOfUsers: "5",
        numberOfEnviroments: "1",
        credits: "200",
        priceYearly: "40",
        supportLevel: "email", // Email support
        features: [
            "Remove AgentUI Badge",
            "Standard Integrations",
            "Unlimited number of apps",
            "In-app code edits",
            "Backend Functions",
            "Unlimited External Users",
            "5 Internal Users",
            "Connect a domain",
            "Email support",
            "2 Enviroments (Development and Production)",
        ],
        featureKeys: [
            "removeAgentUIBadge",
            "standardIntegrations",
            "unlimitedApps",
            "inAppCodeEdits",
            "backendFunctions",
            "unlimitedExternalUsers",
            "internalUsers5",
            "connectDomain",
            "emailSupport",
            "twoEnvironments",
        ],
        enabledFeatures: {
            WHATSAPP: true,
            integrations: true,
            backendFunctions: true,
            exportApp: true,
            inAppCodeEdits: true,
            sharedSecrets: false,
            sharedDatabases: false,
            deepLinking: false,
            sqlIntegration: false,
            multipleEnvironments: false,
            enviroments: true,
            productionLock: false,
            customDomains: true,
            auditLogs: false,
            facebookAds: false,
            clientPortal: false,
            codeScanning: false,
            sso: false,
            dataObfuscation: false,
            designSystem: false,
            complianceOfficer: false,
            aiTesting: false,
        },
        automationExecutions: 2000,
        automation: {
            monthlyLimit: 2000,
            graceMultiplier: 1.5,
            maxConcurrent: 5,
        },
        usageLimits: { maxFiles: 2500, maxDatabaseRows: 25000 },
    },
    {
        name: "Innovator Package",
        nameKey: "innovator",
        id: "price_innovator_monthly", // Monthly price ID - to be updated
        idYearly: "price_innovator_yearly", // Yearly price ID - to be updated
        productId: "prod_TOQNwRpQj69YGE",

        priceIdMonthly: "price_1SRdX2KVJm3st1cQ48EurE57",
        priceIdYearly: "price_1SRdX2KVJm3st1cQvOlc7Y8v",

        numberOfUsers: "10",
        numberOfEnviroments: "2",
        recommended: true,
        descriptionKey: "billing.planDescriptions.innovator",
        description:
            "Our most chosen option by companies that need data integration and scale",
        price: "100",
        priceYearly: "80",
        credits: "400",
        supportLevel: "email", // Email support
        features: [
            "Remove AgentUI Badge",
            "Standard Integrations",
            "SQL Integration",
            "Unlimited number of apps",
            "Backend Functions",
            "In-app code edits",
            "Unlimited External Users",
            "10 Internal Users",
            "Internal Company Portal",
            "Connect a domain",
            "Shared Secrets",
            "Email support",
            "2 Enviroments (Development and Production)",
        ],
        featureKeys: [
            "removeAgentUIBadge",
            "standardIntegrations",
            "sqlIntegration",
            "unlimitedApps",
            "backendFunctions",
            "inAppCodeEdits",
            "unlimitedExternalUsers",
            "internalUsers10",
            "internalCompanyPortal",
            "connectDomain",
            "sharedSecrets",
            "emailSupport",
            "twoEnvironments",
        ],
        enabledFeatures: {
            WHATSAPP: true,
            integrations: true,
            backendFunctions: true,
            exportApp: true,
            inAppCodeEdits: true,
            sharedSecrets: true,
            sharedDatabases: true,
            deepLinking: true,
            facebookAds: true,
            clientPortal: true,
            sqlIntegration: true,
            multipleEnvironments: false,
            enviroments: true,
            productionLock: true,
            customDomains: true,
            auditLogs: false,
            codeScanning: false,
            sso: false,
            dataObfuscation: false,
            designSystem: true,
            complianceOfficer: false,
            aiTesting: false,
        },
        automationExecutions: 10000,
        automation: {
            monthlyLimit: 10000,
            graceMultiplier: 1.5,
            maxConcurrent: 10,
        },
        usageLimits: { maxFiles: 10000, maxDatabaseRows: 100000 },
    },
    {
        name: "Visionary Package",
        nameKey: "visionary",
        id: "price_visionary_monthly", // Monthly price ID - to be updated
        idYearly: "price_visionary_yearly", // Yearly price ID - to be updated
        productId: "prod_TOQPaOwrRpay6u",
        devProductId: "prod_TOQcxyhPL89WbM", // Development product ID

        devPriceIdMonthly: "price_1SRdknKVJm3st1cQZ8vunL1Z",
        devPriceIdYearly: "price_1SRdknKVJm3st1cQZve00Ng9",

        priceIdMonthly: "price_1SdJPpKVJm3st1cQh8kM4GNA",
        priceIdYearly: "price_1SdJQuKVJm3st1cQX9sO2N2a",

        descriptionKey: "billing.planDescriptions.visionary",
        description:
            "Complete transformation for organizations seeking total automation and priority support",
        price: "250",
        priceYearly: "200",
        numberOfUsers: "25",
        numberOfEnviroments: "5",
        credits: "1000",
        ssoUserLimit: 25,
        auditLogRetentionDays: 7,
        codeScanMaxRules: 10,
        supportLevel: "priority", // Priority support
        features: [
            "Remove AgentUI Badge",
            "Standard Integrations",
            "SQL Integration",
            "Unlimited number of apps",
            "Backend Functions",
            "In-app code edits",
            "Internal Company Portal",
            "Connect a domain",
            "Unlimited External Users",
            "25 Internal Users",
            "Shared Secrets",
            "Premium support",
            "Unlimited Enviroments",
        ],
        featureKeys: [
            "removeAgentUIBadge",
            "standardIntegrations",
            "sqlIntegration",
            "unlimitedApps",
            "backendFunctions",
            "inAppCodeEdits",
            "internalCompanyPortal",
            "connectDomain",
            "unlimitedExternalUsers",
            "internalUsers25",
            "sharedSecrets",
            "premiumSupport",
            "unlimitedEnvironments",
        ],
        enabledFeatures: {
            WHATSAPP: true,
            integrations: true,
            backendFunctions: true,
            exportApp: true,
            inAppCodeEdits: true,
            sharedSecrets: true,
            sharedDatabases: true,
            deepLinking: true,
            facebookAds: true,
            clientPortal: true,
            sqlIntegration: true,
            multipleEnvironments: true,
            enviroments: true,
            productionLock: true,
            customDomains: true,
            auditLogs: true,
            codeScanning: true,
            sso: true,
            dataObfuscation: false,
            designSystem: true,
            complianceOfficer: true,
            aiTesting: true,
        },
        automationExecutions: 25000,
        automation: {
            monthlyLimit: 25000,
            graceMultiplier: 1.5,
            maxConcurrent: 20,
        },
        usageLimits: { maxFiles: 50000, maxDatabaseRows: 500000 },
    },
    {
        name: "Build with You",
        nameKey: "visionary",
        hidden: true,
        id: "price_visionary_monthly", // Monthly price ID - to be updated
        idYearly: "price_visionary_yearly", // Yearly price ID - to be updated
        productId: "prod_TPAhwkLQS9JOKC",

        priceIdMonthly: "price_1SSMM9KVJm3st1cQkrosc1Kf",
        priceIdYearly: "price_1SSMM9KVJm3st1cQjVpNhJ4v",

        description: "For large organizations requiring premium support",
        price: "2500",
        priceYearly: "2250",
        numberOfUsers: "25",
        numberOfEnviroments: "9999999999",
        credits: "800",
        ssoUserLimit: 999999999,
        auditLogRetentionDays: 999999999,
        codeScanMaxRules: 999999999,
        supportLevel: "premium", // Premium support with dedicated hours
        supportHours: 15, // 15 hours of hands-on support included
        features: [
            "Everything in the Visionary plan, plus:",
            "Custom integrations",
            "15 hours of dedicated development time",
            "Cancel anytime",
        ],
        featureKeys: [
            "everythingInVisionary",
            "customIntegrations",
            "dedicatedDevTime",
            "cancelAnytime",
        ],
        enabledFeatures: {
            WHATSAPP: true,
            integrations: true,
            backendFunctions: true,
            exportApp: true,
            inAppCodeEdits: true,
            sharedSecrets: true,
            sharedDatabases: true,
            deepLinking: true,
            facebookAds: true,
            clientPortal: true,
            sqlIntegration: true,
            multipleEnvironments: true,
            enviroments: true,
            productionLock: true,
            customDomains: true,
            auditLogs: true,
            codeScanning: true,
            sso: true,
            dataObfuscation: true,
            designSystem: true,
            complianceOfficer: true,
            aiTesting: true,
        },
        automationExecutions: 100000,
        automation: {
            monthlyLimit: 100000,
            graceMultiplier: 1.5,
            maxConcurrent: 50,
        },
        usageLimits: { maxFiles: null, maxDatabaseRows: null },
    },
    {
        name: "Enterprise Plus",
        nameKey: "enterprisePlus",
        id: "enterprise", // Keep as enterprise for identification
        idYearly: "enterprise",
        productId: "prod_enterprise",
        description:
            "For large organizations requiring SSO and on-premise deployment",
        price: "Contact us",
        numberOfUsers: "1999999999",
        enviroments: "9999999999",
        credits: "Unlimited",
        ssoUserLimit: 999999999,
        auditLogRetentionDays: 999999999,
        codeScanMaxRules: 999999999,
        supportLevel: "premium", // Premium support with assigned developer
        features: [
            "Remove AgentUI Badge",
            "All Standard Integrations Included",
            "SQL Integration",
            "Unlimited number of apps",
            "In-app code edits",
            "Connect a domain",
            "Backend Functions",
            "Shared Secrets",
            "Audit Logs",
            "Internal Company Portal",
            "Premium support (Assigned Support Developer)",
            "SSO (Single Sign-On)",
            "On-premise deployment",
            "Unlimited Enviroments",
        ],
        featureKeys: [
            "removeAgentUIBadge",
            "allStandardIntegrations",
            "sqlIntegration",
            "unlimitedApps",
            "inAppCodeEdits",
            "connectDomain",
            "backendFunctions",
            "sharedSecrets",
            "auditLogs",
            "internalCompanyPortal",
            "premiumSupportAssigned",
            "sso",
            "onPremise",
            "unlimitedEnvironments",
        ],
        enabledFeatures: {
            WHATSAPP: true,
            integrations: true,
            backendFunctions: true,
            exportApp: true,
            inAppCodeEdits: true,
            sharedSecrets: true,
            sharedDatabases: true,
            deepLinking: true,
            facebookAds: true,
            clientPortal: true,
            sqlIntegration: true,
            multipleEnvironments: true,
            enviroments: true,
            productionLock: true,
            customDomains: true,
            auditLogs: true,
            codeScanning: true,
            sso: true,
            dataObfuscation: true,
            designSystem: true,
            complianceOfficer: true,
            aiTesting: true,
        },
        automationExecutions: null,
        automation: null,
        usageLimits: { maxFiles: null, maxDatabaseRows: null },
    },
    {
        name: "Advanced",
        nameKey: "price_1RJgPJKVJm3st1cQjvcYbYIs",
        id: "price_1RJgPJKVJm3st1cQjvcYbYIs", // Keep as enterprise for identification
        hidden: true,
        productId: "prod_SE8gWdF5oj79vp",
        priceIdMonthly: "price_1RJgPJKVJm3st1cQjvcYbYIs",

        description:
            "For large organizations requiring SSO and on-premise deployment",
        price: "Contact us",
        numberOfUsers: "250",
        credits: "Unlimited",
        ssoUserLimit: 999999999,
        auditLogRetentionDays: 999999999,
        codeScanMaxRules: 999999999,
        supportLevel: "premium", // Premium support
        features: [],
        enabledFeatures: {
            WHATSAPP: true,
            integrations: true,
            backendFunctions: true,
            exportApp: true,
            inAppCodeEdits: true,
            sharedSecrets: true,
            sharedDatabases: true,
            deepLinking: true,
            facebookAds: true,
            clientPortal: true,
            sqlIntegration: true,
            multipleEnvironments: true,
            enviroments: true,
            productionLock: true,
            customDomains: true,
            auditLogs: true,
            codeScanning: true,
            sso: true,
            dataObfuscation: true,
            designSystem: true,
            complianceOfficer: true,
            aiTesting: true,
        },
        automationExecutions: null,
        automation: null,
        usageLimits: { maxFiles: null, maxDatabaseRows: null },
    },
];

export const getAutomationLimits = (planId) => {
    if (!planId) return { monthlyLimit: 0, graceMultiplier: 1, maxConcurrent: 1 };
    const plan = PLANS.find(
        (p) =>
            p.priceIdMonthly === planId ||
            p.priceIdYearly === planId ||
            p.devPriceIdMonthly === planId ||
            p.devPriceIdYearly === planId
    );
    if (!plan) return { monthlyLimit: 0, graceMultiplier: 1, maxConcurrent: 1 };
    return plan.automation || null;
};

export const getUsageLimits = (planId) => {
    if (!planId) return FREE_TIER_LIMITS;
    const plan = PLANS.find(
        (p) =>
            p.priceIdMonthly === planId ||
            p.priceIdYearly === planId ||
            p.devPriceIdMonthly === planId ||
            p.devPriceIdYearly === planId
    );
    if (!plan) return FREE_TIER_LIMITS;
    return plan.usageLimits || { maxFiles: null, maxDatabaseRows: null };
};

export const getPlanByName = (planName) => {
    return PLANS.find((plan) => plan.name === planName);
};

export const getPlanByPriceId = (priceId) => {
    if (!priceId) return null;
    return PLANS.find(
        (plan) =>
            plan.priceIdMonthly === priceId ||
            plan.priceIdYearly === priceId ||
            plan.devPriceIdMonthly === priceId ||
            plan.devPriceIdYearly === priceId ||
            plan.productId === priceId ||
            plan.devProductId === priceId
    ) || null;
};

export const getSignupPlans = () => {
    // Return plans available for signup (exclude Enterprise plans for now)
    return PLANS.filter(
        (plan) => plan.id !== "enterprise" && plan.nameKey !== "enterprisePlus"
    );
};

export const getAllPlans = () => {
    return PLANS;
};

const getEnabledFeatures = (planId) => {
    const plan = PLANS.find(
        (plan) =>
            (plan.priceIdMonthly === planId && plan.priceIdMonthly) ||
            (plan.priceIdYearly === planId && plan.priceIdYearly) ||
            (plan.devPriceIdMonthly === planId && plan.devPriceIdMonthly) ||
            (plan.devPriceIdYearly === planId && plan.devPriceIdYearly) ||
            plan.productId === planId
    );
    return plan?.enabledFeatures || {};
};

/** Company IDs with feature exceptions (features enabled regardless of plan). */
// cmlz9sgjr1gt0qy1j7nyh300x this company is WSSCWater
const FEATURE_EXCEPTION_COMPANY_IDS = ["cmlz9sgjr1gt0qy1j7nyh300x"];
/** Features granted to FEATURE_EXCEPTION_COMPANY_IDS. */
const FEATURES_ALLOWED_FOR_EXCEPTION_COMPANIES = ["sso", "designSystem"];

const hasFeatureExceptionForCompany = (feature, companyId) => {
    if (!companyId) return false;
    return (
        FEATURE_EXCEPTION_COMPANY_IDS.includes(companyId) &&
        FEATURES_ALLOWED_FOR_EXCEPTION_COMPANIES.includes(feature)
    );
};

export const isFeatureEnabled = (planId, feature, companyId) => {
    if (hasFeatureExceptionForCompany(feature, companyId)) return true;
    if (!planId) return false;
    const enabledFeatures = getEnabledFeatures(planId);
    return enabledFeatures[feature];
};

export const getNumberOfUsers = (planId) => {
    const plan = PLANS.find(
        (plan) =>
            plan.priceIdMonthly === planId ||
            plan.priceIdYearly === planId ||
            plan.devPriceIdMonthly === planId ||
            plan.devPriceIdYearly === planId
    );
    if (plan) return parseInt(plan.numberOfUsers) || 1;
    // Default to Starter plan's user count for free/unmatched plans
    const starterPlan = PLANS.find((p) => p.nameKey === "starter");
    return parseInt(starterPlan?.numberOfUsers) || 3;
};
export const getCustomPlanNumber = (planId, feature) => {
    const plan = PLANS.find(
        (plan) =>
            plan.priceIdMonthly === planId ||
            plan.priceIdYearly === planId ||
            plan.devPriceIdMonthly === planId ||
            plan.devPriceIdYearly === planId
    );
    if (!plan) return 1;
    return parseInt(plan?.[feature]) || 1; // Default to 1 user if plan not found
};
// Hands-on support plans - purchasable separately
// Pricing: $150/hr for 4h, $125/hr for 8h, $115/hr for 16h
const SUPPORT_PLANS = [
    {
        name: "Starter",
        tier: "BASIC_4H",
        hours: 4,
        hourlyRate: 150,
        price: 600, // 4 * $150
        priceIdMonthly: "price_1Sr5LIKVJm3st1cQLdn7sa9x",
        priceIdYearly: "price_1Sr5LIKVJm3st1cQr3UsZuLu",

        devPriceIdMonthly: "price_1Sr5EqKVJm3st1cQgeAt278I",
        devPriceIdYearly: "price_1Sr5EqKVJm3st1cQRoAtTEWe",
        productId: "prod_ToimdTbfFIgYWc",
        devProductId: "prod_ToigSL65pUUyQK",
        description: "Perfect for getting started",
        features: [
            "4 hours of dedicated 1:1 support",
            "Screen sharing sessions",
            "Priority response within 24 hours",
        ],
    },
    {
        name: "Professional",
        tier: "STANDARD_8H",
        hours: 8,
        hourlyRate: 125,
        price: 1000, // 8 * $125
        priceIdMonthly: "price_1Sr5LNKVJm3st1cQ9GCwBIHa",
        priceIdYearly: "price_1Sr5LNKVJm3st1cQRnaNfmvD",
        devPriceIdMonthly: "price_1Sr5FPKVJm3st1cQSZPRGsNG",
        devPriceIdYearly: "price_1Sr5FPKVJm3st1cQGRonioKR",
        productId: "prod_ToimhME8vYt7mm",
        devProductId: "prod_support_8h_dev",
        description: "Best value for growing teams",
        features: [
            "8 hours of dedicated 1:1 support",
            "Screen sharing sessions",
            "Architecture reviews",
            "Priority response within 12 hours",
        ],
    },
    {
        name: "Enterprise",
        tier: "PREMIUM_16H",
        hours: 16,
        hourlyRate: 115,
        price: 1840, // 16 * $115
        priceIdMonthly: "price_1Sr5LQKVJm3st1cQdFkwwAAs",
        priceIdYearly: "price_1Sr5LQKVJm3st1cQyJvHEZIc",
        devPriceIdMonthly: "price_1Sr5FqKVJm3st1cQ3GImaYt9",
        devPriceIdYearly: "price_1Sr5FqKVJm3st1cQPZj8q6n0",
        productId: "prod_ToimJ7nWt5nYyu",
        devProductId: "prod_ToihHhFxEKaAE3",
        description: "For teams needing extensive support",
        features: [
            "16 hours of dedicated 1:1 support",
            "Screen sharing sessions",
            "Architecture reviews",
            "Custom implementation assistance",
            "Priority response within 4 hours",
        ],
    },
];

// LATAM discount coupon ID (50% off for Latin America)
export const LATAM_COUPON_ID = "Dw8c7VtP";

// ISO country codes for the LATAM-eligible region. The server uses this list
// to decide whether to apply LATAM_COUPON_ID. Never trust the client to declare
// LATAM eligibility — derive it from the Stripe customer's verified billing
// country instead.
export const LATAM_COUNTRY_CODES = [
    "AR", // Argentina
    "BO", // Bolivia
    "BR", // Brazil
    "CL", // Chile
    "CO", // Colombia
    "CR", // Costa Rica
    "CU", // Cuba
    "DO", // Dominican Republic
    "EC", // Ecuador
    "SV", // El Salvador
    "GT", // Guatemala
    "HN", // Honduras
    "MX", // Mexico
    "NI", // Nicaragua
    "PA", // Panama
    "PY", // Paraguay
    "PE", // Peru
    "UY", // Uruguay
    "VE", // Venezuela
];

export function isLatamCountryCode(code) {
    if (!code || typeof code !== "string") return false;
    return LATAM_COUNTRY_CODES.includes(code.toUpperCase());
}

// Support plan helper functions
export const getSupportPlanFromPriceId = (priceId) => {
    if (!priceId) return null;
    return SUPPORT_PLANS.find(
        (plan) =>
            plan.priceIdMonthly === priceId ||
            plan.priceIdYearly === priceId ||
            plan.devPriceIdMonthly === priceId ||
            plan.devPriceIdYearly === priceId
    );
};

export const getSupportPlanByTier = (tier) => {
    return SUPPORT_PLANS.find((plan) => plan.tier === tier);
};

export const isSupportPlan = (priceId) => {
    return !!getSupportPlanFromPriceId(priceId);
};

export const getAllSupportPlans = () => {
    return SUPPORT_PLANS;
};

// Get support level from a plan's price ID
export const getSupportLevel = (priceId) => {
    if (!priceId) return "none";
    const plan = PLANS.find(
        (plan) =>
            plan.priceIdMonthly === priceId ||
            plan.priceIdYearly === priceId ||
            plan.devPriceIdMonthly === priceId ||
            plan.devPriceIdYearly === priceId
    );
    return plan?.supportLevel || "none";
};

export const hasSupportAccess = (priceId) => {
    const level = getSupportLevel(priceId);
    return level !== "none";
};

export { PLANS, SUPPORT_PLANS };
